import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { PHONE_DISPLAY, PHONE_TEL } from "@/data/services";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/work", label: "Our Work" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 z-50 w-full border-b backdrop-blur-xl transition-[background-color,box-shadow,border-color] duration-300 ${
                scrolled
                    ? "border-slate-200/80 bg-white/85 shadow-[0_4px_24px_rgba(15,23,42,0.06)]"
                    : "border-white/20 bg-white/70"
            }`}
        >
            <div className="mx-auto flex h-16 md:h-20 max-w-7xl items-center justify-between px-5 md:px-8">
                <Link to="/" data-testid="header-logo-link" aria-label="Digital Circle home">
                    <Logo />
                </Link>

                <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `text-sm font-medium transition-colors duration-200 hover:text-slate-900 ${
                                    isActive ? "text-slate-900" : "text-slate-500"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden items-center gap-5 lg:flex">
                    <a
                        href={PHONE_TEL}
                        data-testid="header-phone-link"
                        className="flex items-center gap-2 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:text-slate-900"
                    >
                        <Phone className="h-4 w-4" />
                        {PHONE_DISPLAY}
                    </a>
                    <Link
                        to="/contact"
                        data-testid="header-quote-button"
                        className="rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-slate-800 hover:shadow-lg"
                    >
                        Get a Quote
                    </Link>
                </div>

                <button
                    data-testid="mobile-menu-button"
                    onClick={() => setOpen(!open)}
                    className="rounded-full p-2 text-slate-900 lg:hidden"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <nav className="flex flex-col gap-1 px-6 py-6">
                            {NAV.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                                    className={({ isActive }) =>
                                        `rounded-xl px-4 py-3 text-base font-medium transition-colors duration-200 ${
                                            isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <a
                                href={PHONE_TEL}
                                data-testid="mobile-phone-link"
                                className="mt-2 flex items-center gap-2 rounded-xl px-4 py-3 text-base font-semibold text-slate-900"
                            >
                                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                            </a>
                            <Link
                                to="/contact"
                                data-testid="mobile-quote-button"
                                className="mt-3 rounded-full bg-slate-900 px-6 py-3.5 text-center text-sm font-semibold text-white"
                            >
                                Get a Quote
                            </Link>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
